<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSectionPermissions } from 'dashboard/composables/useSectionPermissions';

const { t } = useI18n();

const { canEditSection } = useSectionPermissions();

const props = defineProps({
  question: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['review', 'delete']);

const isPending = computed(() => props.question.status === 'pending');
const isApproved = computed(() => props.question.status === 'approved');
const isRejected = computed(() => props.question.status === 'rejected');

const statusConfig = computed(() => {
  if (isApproved.value) {
    return {
      label: t('OMNI.PENDING_QUESTIONS.STATUS_APPROVED'),
      class: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
      icon: 'i-lucide-check-circle',
    };
  }
  if (isRejected.value) {
    return {
      label: t('OMNI.PENDING_QUESTIONS.STATUS_REJECTED'),
      class: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      icon: 'i-lucide-x-circle',
    };
  }
  return {
    label: t('OMNI.PENDING_QUESTIONS.STATUS_PENDING'),
    class: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    icon: 'i-lucide-clock',
  };
});

const formattedDate = computed(() => {
  if (!props.question.created_at) return '';
  return new Date(props.question.created_at).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
});
</script>

<template>
  <div
    class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 hover:border-woot-300 dark:hover:border-woot-700 transition-colors"
  >
    <div class="flex items-start gap-4">
      <!-- Main Content -->
      <div class="flex-1 min-w-0">
        <!-- Header with status and bot -->
        <div class="flex items-center gap-2 mb-2 flex-wrap">
          <span
            :class="statusConfig.class"
            class="text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1"
          >
            <span :class="statusConfig.icon" class="w-3 h-3" />
            {{ statusConfig.label }}
          </span>
          <span
            v-if="question.agent_bot"
            class="text-xs bg-woot-100 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"
          >
            <span class="i-lucide-bot w-3 h-3" />
            {{ question.agent_bot.name }}
          </span>
          <span class="text-xs text-slate-400 dark:text-slate-500">
            {{ formattedDate }}
          </span>
        </div>

        <!-- Question -->
        <div class="mb-3">
          <label
            class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide"
          >
            {{ t('OMNI.PENDING_QUESTIONS.QUESTION') }}
          </label>
          <p class="text-sm text-slate-900 dark:text-slate-100 mt-1">
            {{ question.question }}
          </p>
        </div>

        <!-- Predicted Answer -->
        <div v-if="question.predicted_answer" class="mb-3">
          <label
            class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide"
          >
            {{ t('OMNI.PENDING_QUESTIONS.PREDICTED_ANSWER') }}
          </label>
          <p
            class="text-sm text-slate-700 dark:text-slate-300 mt-1 bg-slate-50 dark:bg-slate-700/50 rounded p-2"
          >
            {{ question.predicted_answer }}
          </p>
        </div>

        <!-- Rejection Reason (if rejected) -->
        <div
          v-if="isRejected && question.rejection_reason"
          class="mb-3 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800"
        >
          <label
            class="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wide"
          >
            {{ t('OMNI.PENDING_QUESTIONS.REJECTION_REASON') }}
          </label>
          <p class="text-sm text-red-700 dark:text-red-300 mt-1">
            {{ question.rejection_reason }}
          </p>
        </div>

        <!-- Reviewed by (if not pending) -->
        <div
          v-if="!isPending && question.reviewed_by"
          class="text-xs text-slate-500 dark:text-slate-400"
        >
          {{ t('OMNI.PENDING_QUESTIONS.REVIEWED_BY') }}:
          {{ question.reviewed_by.name }}
        </div>
      </div>

      <!-- Actions -->
      <div v-if="canEditSection('pending_questions')" class="flex flex-col gap-2">
        <button
          v-if="isPending"
          class="px-3 py-1.5 text-sm font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600 flex items-center gap-1"
          @click="emit('review', question)"
        >
          <span class="i-lucide-eye w-4 h-4" />
          {{ t('OMNI.PENDING_QUESTIONS.REVIEW') }}
        </button>
        <button
          class="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
          :title="t('OMNI.COMMON.DELETE')"
          @click="emit('delete', question.id)"
        >
          <span class="i-lucide-trash w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>

